import { ActionItem } from '../../../interfaces/dashboard.interface';
import { PresetKey } from '../registry/presets';
import { isRolePreset } from './workSummary';

/** One Action Center card the person can hide: a stable id (never the label) plus the label shown in the dialog. */
export interface ActionCardDef { key: string; label: string }

/**
 * The stable key of an item. Items are already keyed by stable ids; the one dynamic family
 * (`payroll.awaiting.<year>-<month>`, one item per month) shares the single key `payroll.awaiting`,
 * so hiding payroll approvals hides every month, including months that appear later.
 */
export const actionCardKey = (id: string): string => (id.startsWith('payroll.awaiting.') ? 'payroll.awaiting' : id);

const LABELS: Record<string, string> = {
    'leave.overdue': 'Leave approvals overdue',
    'leave.pending': 'Leave approvals pending',
    'expense.pending': 'Expense claims awaiting approval',
    'requisition.pending': 'Store requisitions awaiting approval',
    'stock.low': 'Items below reorder level',
    'ar.overdue': 'Receivables more than 60 days overdue',
    'ar.over60': 'Receivables more than 60 days overdue',
    'payroll.awaiting': 'Payroll awaiting approval',
    'repairbill.waiting': 'Repair bills waiting',
    'approvals.waiting': 'Approvals waiting for you',
    'journal.awaiting': 'Journal entries awaiting posting',
    'bill.overdue': 'Overdue supplier bills',
    'bill.hold': 'Bills on approval hold',
    'bank.unreconciled': 'Bank lines to reconcile',
    'compliance.due': 'Compliance deadline coming up',
    'run.draft': 'Payment runs in draft',
    'run.awaiting': 'Payment runs to approve',
    'invoice.overdue': 'Overdue invoices',
    'promise.due': 'Promises to pay falling due',
    'pdc.due': 'Post-dated cheques to deposit',
};

/** The role-board work-summary cards per preset (mirrors buildWorkActions in workSummary.ts). */
const WORK_CARDS: Partial<Record<PresetKey, string[]>> = {
    accountant: ['journal.awaiting', 'bank.unreconciled', 'compliance.due', 'bill.hold', 'ar.over60'],
    apClerk: ['bill.overdue', 'bill.hold', 'run.draft'],
    arClerk: ['invoice.overdue', 'promise.due', 'pdc.due'],
    paymentApprover: ['run.awaiting', 'approvals.waiting', 'journal.awaiting'],
    manager: ['approvals.waiting'],
};

export interface ActionCardGates {
    /** DASHBOARD_VIEW: the leave cards. */
    leave: boolean;
    /** The expense summary widget is visible. */
    expense: boolean;
    /** Inventory module on plus DASHBOARD_INVENTORY_VIEW. */
    store: boolean;
    /** The receivables-ageing widget is visible. */
    receivables: boolean;
    payroll: boolean;
    repairBills: boolean;
}

/**
 * Every card this person's Action Center could ever show, from their role board and the same permission and
 * module gates ActionCenter loads with, whether or not it has items now (the board hides empty cards, so
 * this static list is what lets a person pre-hide one). `shown` (the items on screen right now) is merged in
 * so a card is never missing from the dialog even if this list drifts from the loaders. Role boards list the
 * cards the work summary can produce; a card whose source the person cannot read is listed but never appears.
 */
export function actionCardCatalog(preset: PresetKey | undefined, gates: ActionCardGates, shown: ActionItem[] = []): ActionCardDef[] {
    const keys: string[] = [];
    const add = (...ks: string[]) => ks.forEach(k => { if (!keys.includes(k)) keys.push(k); });
    if (isRolePreset(preset)) {
        if (preset === 'manager') {
            if (gates.leave) add('leave.overdue', 'leave.pending');
            if (gates.expense) add('expense.pending');
            if (gates.store) add('requisition.pending');
        }
        add(...(WORK_CARDS[preset] ?? []));
    } else {
        if (gates.leave) add('leave.overdue', 'leave.pending');
        if (gates.expense) add('expense.pending');
        if (gates.store) add('requisition.pending', 'stock.low');
        if (gates.receivables) add('ar.overdue');
    }
    if (gates.payroll) add('payroll.awaiting');
    if (gates.repairBills) add('repairbill.waiting');
    // Screen order first (the items rendered now), then the catalog cards that have nothing waiting.
    const defs: ActionCardDef[] = [];
    const push = (key: string, fallback: string) => { if (!defs.some(d => d.key === key)) defs.push({ key, label: LABELS[key] ?? fallback }); };
    for (const item of shown) push(actionCardKey(item.id), item.title);
    for (const key of keys) push(key, key);
    return defs;
}
